import { request } from "../api";

export type Platform = "android" | "ios";
export type DeviceStatusFilter = "online" | "recent" | "stale";

export type DeviceLifecycleStatus =
  | "pending_inventory"
  | "awaiting_policy"
  | "active"
  | "disabled"
  | "deprovisioned";

export type AdminDeviceStatusTarget = "active" | "disabled" | "deprovisioned";

export interface DeviceUserEmbed {
  id: string;
  email: string;
  full_name: string;
}

export interface DeviceEnrolleeEmbed {
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  employee_code: string;
  collected_at: string | null;
}

export type ProtectionRevocationReason =
  | "preempted_by_other_vpn"
  | "user_revoked"
  | ""
  | string;

export interface DeviceEmployeeEmbed {
  id: string;
  employee_id: string;
  full_name: string;
  protection_active: boolean;
  last_entry_at: string | null;
  last_exit_at: string | null;
}

export interface DeviceListRow {
  id: string;
  platform: Platform;
  device_uid: string;
  model: string;
  manufacturer: string;
  os_version: string;
  app_version: string;
  last_seen_at: string | null;
  /** Additive backend contract; absent fields remain unknown on older deployments. */
  is_online?: boolean;
  last_policy_fetched_at?: string | null;
  policy_enabled?: boolean;
  is_active: boolean;
  status: DeviceLifecycleStatus;
  user: DeviceUserEmbed | null;
  enrollee: DeviceEnrolleeEmbed | null;
  employee: DeviceEmployeeEmbed | null;
  created_at: string;
  /**
   * Sticky marker set by `DeviceHeartbeatService` when the mobile agent's
   * `OdBloxVpnService.onRevoke()` reports the tunnel was torn down — either
   * because another VPN took the single Android VPN slot
   * (`preempted_by_other_vpn`) or because the user revoked consent
   * (`user_revoked`). Cleared on the next heartbeat where `protection_active=true`.
   * Empty string when no revocation is currently active.
   */
  last_protection_revocation_reason: ProtectionRevocationReason;
  last_protection_revocation_at: string | null;
}

export interface DeviceDetail extends DeviceListRow {
  timezone: string;
  protection_active: boolean | null;
  updated_at: string;
}

export interface DeviceListFilters {
  platform?: Platform;
  status?: DeviceStatusFilter;
  lifecycle_status?: DeviceLifecycleStatus | string;
  is_active?: boolean;
  user?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

interface Paginated<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: T[];
}

export function unwrapList<T>(payload: Paginated<T> | T[]): T[] {
  if (Array.isArray(payload)) return payload;
  return payload.results ?? [];
}

function queryString(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params) as [string, unknown][]) {
    if (v === undefined || v === "" || v === null) continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const devicesApi = {
  list: async (filters: DeviceListFilters = {}) => {
    const data = await request<Paginated<DeviceListRow> | DeviceListRow[]>(
      `/api/admin/v1/devices/${queryString(filters)}`,
    );
    return unwrapList(data);
  },

  listPage: async (filters: DeviceListFilters = {}) => {
    const data = await request<Paginated<DeviceListRow> | DeviceListRow[]>(
      `/api/admin/v1/devices/${queryString(filters)}`,
    );
    const results = unwrapList(data);
    return {
      results,
      complete: Array.isArray(data) || (!data.next && (data.count ?? results.length) <= results.length),
    };
  },

  /** Persistent override, never a lifecycle disable. Requires policy_enabled readback. */
  setPolicyEnabled: (id: string, policy_enabled: boolean) =>
    request<DeviceDetail>(`/api/admin/v1/devices/${id}/set-policy-enabled/`, {
      method: "POST",
      json: { policy_enabled },
    }),

  get: (id: string) => request<DeviceDetail>(`/api/admin/v1/devices/${id}/`),

  forceRefresh: (id: string) =>
    request<ForceRefreshResult>(`/api/admin/v1/devices/${id}/force-refresh/`, {
      method: "POST",
      json: {},
    }),

  setStatus: (id: string, status: AdminDeviceStatusTarget) =>
    request<DeviceListRow>(`/api/admin/v1/devices/${id}/set-status/`, {
      method: "POST",
      json: { status },
    }),

  bulkForceRefresh: (deviceIds: string[]) =>
    request<BulkForceRefreshResult>(`/api/admin/v1/devices/bulk-force-refresh/`, {
      method: "POST",
      json: { device_ids: deviceIds },
    }),

  bulkSetStatus: (deviceIds: string[], status: AdminDeviceStatusTarget) =>
    request<BulkSetStatusResult>(`/api/admin/v1/devices/bulk-set-status/`, {
      method: "POST",
      json: { device_ids: deviceIds, status },
    }),

  getWorkSchedule: (id: string) =>
    request<DeviceWorkScheduleResolution>(`/api/admin/v1/devices/${id}/work-schedule/`),
};

export type DeviceWorkScheduleSource =
  | "device_assignment"
  | "user_assignment"
  | "device_group_assignment"
  | "company_fallback"
  | "none";

export interface DeviceWorkScheduleResolution {
  profile_id: string | null;
  profile_name: string;
  source: DeviceWorkScheduleSource;
  assignment_id: string | null;
  target_label: string | null;
  is_company_fallback: boolean;
}

export interface ForceRefreshResult {
  device_id: string;
  policy_id: string;
  version: number;
  created: boolean;
  next_poll_will_fetch_latest: boolean;
}

export interface BulkFailedEntry {
  device_id: string;
  error: string;
}

export interface BulkForceRefreshResult {
  requested: number;
  matched: number;
  succeeded: string[];
  failed: BulkFailedEntry[];
}

export interface BulkSetStatusResult {
  requested: number;
  matched: number;
  target_status: AdminDeviceStatusTarget;
  succeeded: { device_id: string; from_status: DeviceLifecycleStatus }[];
  skipped: string[];
  failed: BulkFailedEntry[];
}
